import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Bell, AlertCircle, Clock, UserX, RefreshCw, ChevronDown, ChevronUp, Loader2, History, FileWarning, ShieldAlert, Wifi } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { toast } from '../../hooks/use-toast';

const API = process.env.REACT_APP_BACKEND_URL;

const SECTIONS = [
  { key: 'missed_clockouts', label: 'Missed clock-outs', icon: Clock, color: 'amber' },
  { key: 'late_arrivals',    label: 'Late arrivals',     icon: AlertCircle, color: 'orange' },
  { key: 'absences',         label: 'Absences',          icon: UserX, color: 'rose' },
];

const COLOR_CLASSES = {
  amber:  { bg: 'bg-amber-50',  border: 'border-amber-200',  text: 'text-amber-700',  badge: 'bg-amber-100 text-amber-800' },
  orange: { bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-700', badge: 'bg-orange-100 text-orange-800' },
  rose:   { bg: 'bg-rose-50',   border: 'border-rose-200',   text: 'text-rose-700',   badge: 'bg-rose-100 text-rose-800' },
};

const DailyDigest = () => {
  const [digests, setDigests] = useState([]);
  const [date, setDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [forbidden, setForbidden] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [docExpiry, setDocExpiry] = useState({ count: 0, by_doc_type: {}, pushes: [] });
  const [showDocExpiry, setShowDocExpiry] = useState(false);
  const [showFlagged, setShowFlagged] = useState(false);
  const [showStale, setShowStale] = useState(false);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [digestRes, docRes] = await Promise.all([
        axios.get(`${API}/api/hr-digest/latest`, { headers }),
        axios.get(`${API}/api/hr-digest/doc-expiry-pushes`, { headers }).catch(() => ({ data: { count: 0, by_doc_type: {}, pushes: [] } })),
      ]);
      setDigests(digestRes.data.digests || []);
      setDate(digestRes.data.date || '');
      setDocExpiry(docRes.data || { count: 0, by_doc_type: {}, pushes: [] });
      setForbidden(false);
    } catch (e) {
      if (e.response?.status === 403) setForbidden(true);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  const runNow = async () => {
    setRefreshing(true);
    try {
      await axios.post(`${API}/api/hr-digest/run-now`, {}, { headers });
      await load();
      toast({ title: 'Digest refreshed' });
    } catch {
      toast({ title: 'Refresh failed', variant: 'destructive' });
    } finally {
      setRefreshing(false);
    }
  };

  const toggleHistory = async () => {
    const next = !showHistory;
    setShowHistory(next);
    if (next && history.length === 0) {
      setHistoryLoading(true);
      try {
        const res = await axios.get(`${API}/api/hr-digest/list`, { headers, params: { days: 7 } });
        setHistory(res.data.digests || []);
      } catch {
        toast({ title: 'Failed to load history', variant: 'destructive' });
      } finally {
        setHistoryLoading(false);
      }
    }
  };

  if (forbidden) return null;

  const totalCount = digests.reduce((acc, d) => acc + (d.counts?.total || 0), 0);
  const latestSlot = digests.length ? digests[digests.length - 1] : null;
  const flaggedItems = (latestSlot?.flagged_punches) || [];
  const flaggedOff = latestSlot?.counts?.flagged_offnetwork || 0;
  const faceStats = latestSlot?.face_stats || null;
  const staleApprovals = latestSlot?.stale_approvals || { count: 0, items: [], threshold_days: 7, escalated_count: 0 };
  const ukDate = (iso) => {
    if (!iso) return '';
    try {
      const [y, m, d] = iso.split('-');
      return `${d}/${m}/${y}`;
    } catch { return iso; }
  };

  return (
    <Card className="border-violet-200 shadow-sm" data-testid="hr-digest-card">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <CardTitle className="flex items-center gap-2 text-base">
            <Bell className="h-4 w-4 text-violet-600" />
            HR Daily Digest
            <span className="text-xs text-gray-500 font-normal">— {ukDate(date)} (UAE)</span>
            {totalCount > 0 && (
              <Badge variant="destructive" className="ml-1" data-testid="hr-digest-total-badge">
                {totalCount}
              </Badge>
            )}
            {flaggedItems.length > 0 && (
              <Badge className="ml-1 bg-rose-600 hover:bg-rose-600 text-white gap-1" data-testid="hr-digest-flagged-badge">
                <ShieldAlert className="h-3 w-3" /> {flaggedItems.length} flagged
              </Badge>
            )}
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={toggleHistory}
              className="h-8 gap-1.5"
              data-testid="hr-digest-history-toggle"
            >
              <History className="h-3.5 w-3.5" />
              {showHistory ? 'Hide' : 'History'}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={runNow}
              disabled={refreshing || loading}
              className="h-8 gap-1.5"
              data-testid="hr-digest-refresh"
            >
              {refreshing
                ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                : <RefreshCw className="h-3.5 w-3.5" />}
              Refresh
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setCollapsed(c => !c)}
              className="h-8 w-8 p-0"
              data-testid="hr-digest-toggle"
            >
              {collapsed ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
            </Button>
          </div>
        </div>
      </CardHeader>
      {!collapsed && (
        <CardContent className="pt-0">
          {loading ? (
            <div className="flex items-center justify-center py-6 text-gray-500 text-sm">
              <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading digest…
            </div>
          ) : digests.length === 0 ? (
            <div className="py-6 text-center text-gray-500 text-sm">
              No digest generated yet for today. Hit <strong>Refresh</strong> to build one now.
              <div className="text-xs mt-1">Auto-runs at 08:00 and 18:00 UAE.</div>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
                {digests.map(d => (
                  <span
                    key={d.slot}
                    className={`px-2 py-0.5 rounded border ${d.slot === (latestSlot?.slot) ? 'border-violet-400 bg-violet-50 text-violet-700 font-semibold' : 'border-gray-200'}`}
                  >
                    {d.slot === 'morning' ? '🌅 Morning' : '🌇 Evening'} · {d.generated_at_uae}
                  </span>
                ))}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {SECTIONS.map(sec => {
                  const items = (latestSlot?.[sec.key]) || [];
                  const c = COLOR_CLASSES[sec.color];
                  const Icon = sec.icon;
                  return (
                    <div
                      key={sec.key}
                      className={`rounded-lg border ${c.border} ${c.bg} p-3`}
                      data-testid={`hr-digest-section-${sec.key}`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className={`flex items-center gap-1.5 text-sm font-semibold ${c.text}`}>
                          <Icon className="h-4 w-4" />
                          {sec.label}
                        </div>
                        <span className={`text-xs font-bold px-2 py-0.5 rounded ${c.badge}`}>
                          {items.length}
                        </span>
                      </div>
                      {items.length === 0 ? (
                        <div className="text-xs text-gray-500 italic">None</div>
                      ) : (
                        <ul className="space-y-1 max-h-40 overflow-auto pr-1">
                          {items.slice(0, 10).map((it, i) => (
                            <li key={i} className="text-xs text-gray-700 leading-snug">
                              <span className="font-medium">{it.employee_name || '—'}</span>
                              <span className="text-gray-500"> · {it.employee_code}</span>
                              {it.department && <span className="text-gray-400"> · {it.department}</span>}
                              {sec.key === 'missed_clockouts' && it.elapsed_hours != null && (
                                <span className="ml-1 text-amber-700 font-mono">({it.elapsed_hours}h)</span>
                              )}
                            </li>
                          ))}
                          {items.length > 10 && (
                            <li className="text-[11px] text-gray-500 italic">+{items.length - 10} more…</li>
                          )}
                        </ul>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Personal document expiry pushes — sent today by the 08:15 UAE
                  scheduler (one-time per employee + expiry-date). */}
              <div
                className="rounded-lg border border-sky-200 bg-sky-50/60 p-3"
                data-testid="hr-digest-doc-expiry-strip"
              >
                <button
                  type="button"
                  onClick={() => setShowDocExpiry(s => !s)}
                  className="w-full flex items-center justify-between text-left"
                  data-testid="doc-expiry-toggle"
                >
                  <div className="flex items-center gap-2 text-sm font-semibold text-sky-800">
                    <FileWarning className="h-4 w-4" />
                    Document expiry pushes today
                    {docExpiry.count > 0 ? (
                      <span className="text-xs font-bold px-2 py-0.5 rounded bg-sky-100 text-sky-800" data-testid="doc-expiry-count">
                        {docExpiry.count} notified
                      </span>
                    ) : (
                      <span className="text-xs font-normal text-gray-500">— none today</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {/* Per-doc-type chips */}
                    {Object.entries(docExpiry.by_doc_type || {}).map(([dt, n]) => (
                      <span key={dt} className="text-[10px] uppercase tracking-wide bg-white border border-sky-200 text-sky-700 rounded px-1.5 py-0.5 font-mono">
                        {dt}: {n}
                      </span>
                    ))}
                    {docExpiry.count > 0 && (showDocExpiry ? <ChevronUp className="h-4 w-4 text-sky-700" /> : <ChevronDown className="h-4 w-4 text-sky-700" />)}
                  </div>
                </button>
                {showDocExpiry && docExpiry.count > 0 && (
                  <div className="mt-3 overflow-x-auto" data-testid="doc-expiry-table">
                    <table className="w-full text-xs">
                      <thead className="text-[10px] uppercase text-sky-700 border-b border-sky-200">
                        <tr>
                          <th className="text-left py-1.5 pr-3">Employee</th>
                          <th className="text-left py-1.5 pr-3">Document</th>
                          <th className="text-left py-1.5 pr-3">Expires</th>
                          <th className="text-right py-1.5">Days left</th>
                        </tr>
                      </thead>
                      <tbody>
                        {docExpiry.pushes.slice(0, 20).map((p, i) => (
                          <tr key={i} className="border-b border-sky-100 last:border-0 hover:bg-white/60">
                            <td className="py-1.5 pr-3 font-medium">{p.employee_code || '—'}</td>
                            <td className="py-1.5 pr-3">{p.doc_type}</td>
                            <td className="py-1.5 pr-3 font-mono">{(() => { const [y, m, d] = (p.expiry_date || '').split('-'); return `${d}/${m}/${y}`; })()}</td>
                            <td className="py-1.5 text-right font-mono">
                              <span className={p.days_left_at_send <= 14 ? 'text-rose-600 font-bold' : p.days_left_at_send <= 30 ? 'text-orange-600' : 'text-sky-700'}>
                                {p.days_left_at_send}
                              </span>
                            </td>
                          </tr>
                        ))}
                        {docExpiry.pushes.length > 20 && (
                          <tr><td colSpan="4" className="py-1 text-center text-[11px] text-gray-500 italic">+{docExpiry.pushes.length - 20} more notified today…</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Face auto-verify rate today (mirrors the dashboard stat + email) */}
              {faceStats && faceStats.attempted > 0 && (() => {
                const r = faceStats.rate;
                const tone = r >= 90 ? 'emerald' : r >= 75 ? 'amber' : 'rose';
                const cls = {
                  emerald: 'border-emerald-200 bg-emerald-50/70 text-emerald-800',
                  amber: 'border-amber-200 bg-amber-50/70 text-amber-800',
                  rose: 'border-rose-300 bg-rose-50/70 text-rose-800',
                }[tone];
                const problems = (faceStats.by_station || []).filter(s => s.rate !== null && s.rate < 90 && s.attempted >= 2);
                return (
                  <div className={`rounded-lg border p-3 flex flex-wrap items-center gap-x-3 gap-y-1 ${cls}`} data-testid="hr-digest-face-strip">
                    <span className="text-[10px] uppercase tracking-wide font-semibold">Face auto-verify today</span>
                    <span className="text-lg font-bold" data-testid="hr-digest-face-rate">{r}%</span>
                    <span className="text-[11px] text-gray-500">{faceStats.verified}/{faceStats.attempted} verified · {faceStats.unverified} couldn't verify · {faceStats.mismatch} mismatch</span>
                    {problems.length > 0 && (
                      <span className="text-[11px] w-full mt-0.5">⚠ Check camera/lighting: <b>{problems.map(s => `${s.station} ${s.rate}%`).join(' · ')}</b></span>
                    )}
                  </div>
                );
              })()}

              {/* Flagged punches today (anti-spoofing) — off-network / face
                  mismatch / no-GPS clock-ins auto-flagged today. */}
              <div
                className={`rounded-lg border p-3 ${flaggedItems.length > 0 ? 'border-rose-300 bg-rose-50/70' : 'border-gray-200 bg-gray-50/60'}`}
                data-testid="hr-digest-flagged-strip"
              >
                <button
                  type="button"
                  onClick={() => setShowFlagged(s => !s)}
                  className="w-full flex items-center justify-between text-left"
                  data-testid="flagged-strip-toggle"
                >
                  <div className="flex items-center gap-2 text-sm font-semibold text-rose-800">
                    <ShieldAlert className="h-4 w-4" />
                    Flagged punches today
                    {flaggedItems.length > 0 ? (
                      <span className="text-xs font-bold px-2 py-0.5 rounded bg-rose-100 text-rose-800" data-testid="flagged-strip-count">
                        {flaggedItems.length} flagged
                      </span>
                    ) : (
                      <span className="text-xs font-normal text-gray-500">— none today</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {flaggedOff > 0 && (
                      <span className="text-[10px] uppercase tracking-wide bg-rose-600 text-white rounded px-1.5 py-0.5 font-semibold flex items-center gap-1" data-testid="flagged-strip-offnetwork">
                        <Wifi className="h-3 w-3" /> {flaggedOff} off-network
                      </span>
                    )}
                    {flaggedItems.length > 0 && (showFlagged ? <ChevronUp className="h-4 w-4 text-rose-700" /> : <ChevronDown className="h-4 w-4 text-rose-700" />)}
                  </div>
                </button>
                {showFlagged && flaggedItems.length > 0 && (
                  <div className="mt-3 overflow-x-auto" data-testid="flagged-strip-table">
                    <table className="w-full text-xs">
                      <thead className="text-[10px] uppercase text-rose-700 border-b border-rose-200">
                        <tr>
                          <th className="text-left py-1.5 pr-3">When</th>
                          <th className="text-left py-1.5 pr-3">Employee</th>
                          <th className="text-left py-1.5 pr-3">Station</th>
                          <th className="text-left py-1.5 pr-3">Flags</th>
                          <th className="text-left py-1.5">Network IP</th>
                        </tr>
                      </thead>
                      <tbody>
                        {flaggedItems.slice(0, 20).map((f, i) => (
                          <tr key={i} className="border-b border-rose-100 last:border-0 hover:bg-white/60">
                            <td className="py-1.5 pr-3 font-mono whitespace-nowrap">
                              {(() => { try { return new Date(f.timestamp).toLocaleString('en-GB', { timeZone: 'Asia/Dubai', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false }).replace(',', ' '); } catch { return f.timestamp; } })()}
                              <span className="ml-1 text-gray-400">{f.type === 'in' ? 'IN' : 'OUT'}</span>
                            </td>
                            <td className="py-1.5 pr-3 font-medium">{f.employee_name}<span className="text-gray-400"> · {f.employee_code}</span></td>
                            <td className="py-1.5 pr-3">{f.branch_name || '—'}</td>
                            <td className="py-1.5 pr-3">
                              <span className="flex flex-wrap gap-1">
                                {(f.flags || []).map((t, j) => (
                                  <span key={j} className={`px-1.5 py-0.5 rounded border text-[10px] ${t === 'Off-network' ? 'border-rose-300 bg-rose-100 text-rose-700' : 'border-gray-200 bg-white text-gray-600'}`}>{t}</span>
                                ))}
                              </span>
                            </td>
                            <td className="py-1.5 font-mono text-[11px] text-gray-600">{f.client_ip || '—'}</td>
                          </tr>
                        ))}
                        {flaggedItems.length > 20 && (
                          <tr><td colSpan="5" className="py-1 text-center text-[11px] text-gray-500 italic">+{flaggedItems.length - 20} more flagged today…</td></tr>
                        )}
                      </tbody>
                    </table>
                    <p className="text-[10px] text-gray-400 mt-1">Open <b>Clock In/Out → Flagged</b> to review or acknowledge. HR is also emailed a digest when anything is flagged.</p>
                  </div>
                )}
              </div>

              {/* Stale leave approvals — pending beyond the escalation threshold. */}
              <div
                className={`rounded-lg border p-3 ${staleApprovals.count > 0 ? 'border-amber-300 bg-amber-50/70' : 'border-gray-200 bg-gray-50/60'}`}
                data-testid="hr-digest-stale-strip"
              >
                <button
                  type="button"
                  onClick={() => setShowStale(s => !s)}
                  className="w-full flex items-center justify-between text-left"
                  data-testid="stale-strip-toggle"
                >
                  <div className="flex items-center gap-2 text-sm font-semibold text-amber-800">
                    <AlertCircle className="h-4 w-4" />
                    Stale leave approvals
                    {staleApprovals.count > 0 ? (
                      <span className="text-xs font-bold px-2 py-0.5 rounded bg-amber-100 text-amber-800" data-testid="stale-strip-count">
                        {staleApprovals.count} pending &gt; {staleApprovals.threshold_days}d
                      </span>
                    ) : (
                      <span className="text-xs font-normal text-gray-500">— none</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {staleApprovals.escalated_count > 0 && (
                      <span className="text-[10px] uppercase tracking-wide bg-amber-600 text-white rounded px-1.5 py-0.5 font-semibold" data-testid="stale-strip-escalated">
                        {staleApprovals.escalated_count} escalated to HR
                      </span>
                    )}
                    {staleApprovals.count > 0 && (showStale ? <ChevronUp className="h-4 w-4 text-amber-700" /> : <ChevronDown className="h-4 w-4 text-amber-700" />)}
                  </div>
                </button>
                {showStale && staleApprovals.count > 0 && (
                  <div className="mt-3 overflow-x-auto" data-testid="stale-strip-table">
                    <table className="w-full text-xs">
                      <thead className="text-[10px] uppercase text-amber-700 border-b border-amber-200">
                        <tr>
                          <th className="text-left py-1.5 pr-3">Employee</th>
                          <th className="text-left py-1.5 pr-3">Leave</th>
                          <th className="text-left py-1.5 pr-3">Dates</th>
                          <th className="text-right py-1.5 pr-3">Pending</th>
                          <th className="text-left py-1.5">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {staleApprovals.items.slice(0, 20).map((it, i) => (
                          <tr key={i} className="border-b border-amber-100 last:border-0 hover:bg-white/60">
                            <td className="py-1.5 pr-3 font-medium">{it.employee_name}<span className="text-gray-400"> · {it.employee_code}</span></td>
                            <td className="py-1.5 pr-3">{it.leave_type} · {it.days}d</td>
                            <td className="py-1.5 pr-3 whitespace-nowrap">{it.start_date} → {it.end_date}</td>
                            <td className="py-1.5 pr-3 text-right font-bold text-amber-700">{it.age_days}d</td>
                            <td className="py-1.5">
                              {it.escalated ? (
                                <span className="px-1.5 py-0.5 rounded border border-amber-300 bg-amber-100 text-amber-700 text-[10px]">Escalated to HR</span>
                              ) : (
                                <span className="px-1.5 py-0.5 rounded border border-rose-200 bg-rose-50 text-rose-600 text-[10px]">Awaiting manager</span>
                              )}
                            </td>
                          </tr>
                        ))}
                        {staleApprovals.items.length > 20 && (
                          <tr><td colSpan="5" className="py-1 text-center text-[11px] text-gray-500 italic">+{staleApprovals.items.length - 20} more…</td></tr>
                        )}
                      </tbody>
                    </table>
                    <p className="text-[10px] text-gray-400 mt-1">Requests pending longer than the threshold are auto-forwarded to HR by the weekly job. Action them in <b>Leave Management</b>.</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {showHistory && (
            <div className="mt-4 pt-4 border-t border-gray-200" data-testid="hr-digest-history-panel">
              <div className="text-xs font-semibold text-gray-600 mb-2 flex items-center gap-1.5">
                <History className="h-3.5 w-3.5" /> Last 7 days
              </div>
              {historyLoading ? (
                <div className="flex items-center justify-center py-3 text-gray-500 text-xs">
                  <Loader2 className="h-3.5 w-3.5 animate-spin mr-2" /> Loading history…
                </div>
              ) : history.length === 0 ? (
                <p className="text-xs text-gray-500 italic">No digests recorded yet.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="text-[10px] uppercase text-gray-500 border-b">
                      <tr>
                        <th className="text-left py-1.5 pr-3">Date</th>
                        <th className="text-left py-1.5 pr-3">Slot</th>
                        <th className="text-right py-1.5 pr-3">Missed</th>
                        <th className="text-right py-1.5 pr-3">Late</th>
                        <th className="text-right py-1.5 pr-3">Absent</th>
                        <th className="text-right py-1.5">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {history.map(h => {
                        const c = h.counts || {};
                        const total = c.total || 0;
                        const [y, m, d] = (h.date || '').split('-');
                        return (
                          <tr key={`${h.date}-${h.slot}`} className="border-b last:border-0 hover:bg-violet-50/40">
                            <td className="py-1.5 pr-3 font-mono">{d}/{m}/{y}</td>
                            <td className="py-1.5 pr-3">
                              <span className={`px-1.5 py-0.5 rounded text-[10px] ${h.slot === 'morning' ? 'bg-amber-50 text-amber-700' : 'bg-indigo-50 text-indigo-700'}`}>
                                {h.slot === 'morning' ? '🌅 Morning' : '🌇 Evening'}
                              </span>
                            </td>
                            <td className="py-1.5 pr-3 text-right font-mono">{c.missed_clockouts || 0}</td>
                            <td className="py-1.5 pr-3 text-right font-mono">{c.late_arrivals || 0}</td>
                            <td className="py-1.5 pr-3 text-right font-mono">{c.absences || 0}</td>
                            <td className="py-1.5 text-right">
                              <span className={`font-bold ${total > 0 ? 'text-rose-600' : 'text-emerald-600'}`}>
                                {total}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
};

export default DailyDigest;
